import { type FC } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { BsLink45Deg } from 'react-icons/bs';
import cx from 'clsx';

import { type Artifact } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { artifactToArtifactProperties } from '@editor2/mappers/element';
import { artifactElement } from './styles';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';
import { elementCommonStyles } from '@editor2/styles/element';

interface Props {
	element: Artifact;
	courseId: number;
}

const ArtifactElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };
	const debouncedDispatch = useDebouncedDispatch();

	const [artifactProperties, handleLocalChange] = useDebounceProtectedState(
		element,
		artifactToArtifactProperties
	);

	const handleChange = (value) => {
		handleLocalChange(value);
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const handleEmbedCodeChange = (value: string) => handleChange({ embed_code: value });

	const artifactTypes = [
		'Article',
		'Audio',
		'Video',
		'Download',
		'PdfDownload',
		'DocxDownload',
		'XlsxDownload'
	];

	return (
		<Form className={cx(artifactElement, elementCommonStyles)}>
			<Form.Group>
				<Form.Label>Title</Form.Label>
				<Form.Control
					value={artifactProperties.title}
					placeholder="Artifact title"
					onChange={(e) => handleChange({ title: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Type</Form.Label>
				<Form.Control
					as="select"
					className="select"
					value={artifactProperties.type.split('::').slice(-1)[0]}
					onChange={(e) => handleChange({ type: `NG::Artifacts::${e.target.value}` })}>
					{artifactTypes.map((type) => (
						<option key={type} value={type}>
							{type}
						</option>
					))}
				</Form.Control>
			</Form.Group>

			<div className="block">
				<Form.Group>
					<Form.Label>URL</Form.Label>
					<InputGroup>
						<InputGroup.Prepend>
							<InputGroup.Text>
								<BsLink45Deg />
							</InputGroup.Text>
						</InputGroup.Prepend>
						<Form.Control
							value={artifactProperties.artifact_url}
							placeholder="Artifact URL"
							onChange={(e) => handleChange({ artifact_url: e.target.value })}
						/>
					</InputGroup>
				</Form.Group>
				<Form.Group>
					<Form.Label>Backup URL</Form.Label>
					<InputGroup>
						<InputGroup.Prepend>
							<InputGroup.Text>
								<BsLink45Deg />
							</InputGroup.Text>
						</InputGroup.Prepend>
						<Form.Control
							value={artifactProperties.artifact_backup_url}
							placeholder="Artifact Backup URL"
							onChange={(e) => handleChange({ artifact_backup_url: e.target.value })}
						/>
					</InputGroup>
				</Form.Group>

				<Form.Group>
					<Form.Label>Transcript URL</Form.Label>
					<InputGroup>
						<InputGroup.Prepend>
							<InputGroup.Text>
								<BsLink45Deg />
							</InputGroup.Text>
						</InputGroup.Prepend>
						<Form.Control
							value={artifactProperties.transcript_url}
							placeholder="Transcript URL"
							onChange={(e) => handleChange({ transcript_url: e.target.value })}
						/>
					</InputGroup>
				</Form.Group>
			</div>

			<div className="block">
				<Form.Group>
					<Form.Label>Embed code</Form.Label>
					<CodeMirrorHTML value={artifactProperties.embed_code} onChange={handleEmbedCodeChange} />
				</Form.Group>
			</div>

			<div className="block">
				<Form.Group>
					<Form.Label>Credit(s)</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						value={artifactProperties.credits}
						placeholder="Artifact credits/source"
						onChange={(e) => handleChange({ credits: e.target.value })}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>Citation</Form.Label>
					<Form.Control
						as="textarea"
						rows={2}
						value={artifactProperties.citation}
						placeholder="Artifact citation"
						onChange={(e) => handleChange({ citation: e.target.value })}
					/>
				</Form.Group>
			</div>
		</Form>
	);
};

export default ArtifactElement;
